// client
import { ApolloCache, ApolloClient, InMemoryCache, useApolloClient } from '@apollo/client';
import { WebSocketLink } from '@apollo/client/link/ws';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { AsyncStorageWrapper, persistCache } from 'apollo3-cache-persist';
import * as Auth from 'aws-amplify/auth';
import { useEffect, useState } from 'react';

const url = 'wss://iolmatch.hasura.app/v1/graphql';
const httpsUrl = 'https://iolmatch.hasura.app/v1/graphql';
const roleClients: { [k: string]: ApolloClient<object> } = {};

export const useRoleClient = (role: string) => {
  const [client, setClient] = useState<ApolloClient<object> | undefined>();
  const baseClient = useApolloClient();

  useEffect(() => {
    let isLoaded = true;
    (async () => {
      if (roleClients[role]) {
        if (isLoaded) {
          setClient(roleClients[role]);
        }
      } else {
        const clientLocal = await getRoleClient(
          role,
          role == 'admin' ? new InMemoryCache() : baseClient.cache
        );
        roleClients[role] = clientLocal;
        if (isLoaded) {
          setClient(clientLocal);
        }
      }
    })();
    return () => {
      isLoaded = false;
    };
  }, [role]);

  return client;
};

export const getRoleClient = async (role: string, cache?: InMemoryCache | ApolloCache<object>) => {
  const session = await Auth.fetchAuthSession();

  // Continue setting up Apollo as usual.
  return new ApolloClient({
    uri: httpsUrl,
    headers: {
      Authorization: `Bearer ${session.tokens?.idToken?.toString()}`,
      'x-hasura-role': role,
    },
    cache: cache ? cache : new InMemoryCache(),
    defaultOptions: {
      mutate: {
        fetchPolicy: 'network-only',
      },
      query: {
        fetchPolicy: 'cache-first',
      },
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
    },
    devtools: {
      enabled: true,
      name: 'roleclient_' + role,
    },
  });
};

export const getAdminLink = () => {
  return new WebSocketLink({
    uri: url,
    options: {
      reconnect: true,
      connectionParams: async () => {
        const session = await Auth.fetchAuthSession();
        return {
          headers: {
            Authorization: `Bearer ${session.tokens?.idToken?.toString()}`,
            'x-hasura-role': 'admin',
          },
        };
      },
    },
  });
};

export const getNormalLink = () => {
  return new WebSocketLink({
    uri: url,
    options: {
      reconnect: true,
      connectionParams: async () => {
        const session = await Auth.fetchAuthSession();
        return {
          headers: {
            Authorization: `Bearer ${session.tokens?.idToken?.toString()}`,
          },
        };
      },
    },
  });
};

const getClient = async () => {
  const cache = new InMemoryCache({
    canonizeResults: true,
  });

  // await before instantiating ApolloClient, else queries might run before the cache is persisted
  await persistCache({
    cache,
    storage: new AsyncStorageWrapper(AsyncStorage),
  });

  // Continue setting up Apollo as usual.
  return new ApolloClient({
    link: getNormalLink(),
    cache: cache,
    defaultOptions: {
      mutate: {
        fetchPolicy: 'network-only',
      },
      query: {
        fetchPolicy: 'cache-first',
      },
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
    },
  });
};

export default getClient;
